/**
 * CyberSells Toast Notification System
 * Displays achievement unlocks, level-ups, and other notifications
 */

(function() {
  'use strict';

  const TOAST_DURATION = 4000; // 4 seconds
  const TOAST_ANIMATION_DURATION = 300; // ms

  /**
   * Create toast system
   */
  function createToastSystem(ctx) {
    let toastContainer = null;
    let toastQueue = [];
    let currentToast = null;
    let isShowing = false;

    /**
     * Initialize toast container
     */
    function initContainer() {
      if (toastContainer) return;
      
      // Safe no-op if body not available
      if (!document.body) {
        if (window.cyberSellsConfig?.debug) {
          console.warn('[CyberSells] Toast: document.body not available');
        }
        return;
      }

      toastContainer = document.createElement('div');
      toastContainer.className = 'cs-toast-container';
      toastContainer.setAttribute('aria-live', 'polite');
      toastContainer.setAttribute('aria-atomic', 'true');
      document.body.appendChild(toastContainer);
    }

    /**
     * Create toast element
     */
    function createToastElement(type, data) {
      const toast = document.createElement('div');
      toast.className = `cs-toast cs-toast--${type}`;
      toast.setAttribute('role', 'status');

      let content = '';

      if (type === 'achievement') {
        content = `
          <div class="cs-toast__icon">${data.icon || '??'}</div>
          <div class="cs-toast__content">
            <div class="cs-toast__label">Achievement Unlocked!</div>
            <div class="cs-toast__title">${escapeHtml(data.title)}</div>
            <div class="cs-toast__description">${escapeHtml(data.description)}</div>
            ${data.xpReward ? `<div class="cs-toast__xp">+${data.xpReward} XP</div>` : ''}
          </div>
        `;
      } else if (type === 'levelup') {
        content = `
          <div class="cs-toast__icon">??</div>
          <div class="cs-toast__content">
            <div class="cs-toast__label">Level Up!</div>
            <div class="cs-toast__title">Level ${data.newLevel}</div>
            <div class="cs-toast__description">You've reached level ${data.newLevel}!</div>
          </div>
        `;
      } else if (type === 'xp') {
        content = `
          <div class="cs-toast__icon">?</div>
          <div class="cs-toast__content">
            <div class="cs-toast__title">+${data.xp} XP</div>
            <div class="cs-toast__description">${data.message || 'Experience gained'}</div>
          </div>
        `;
      }

      toast.innerHTML = content;
      return toast;
    }

    /**
     * Show a toast notification
     */
    function showToast(type, data, duration = TOAST_DURATION) {
      initContainer();
      
      // Safe no-op if container couldn't be created
      if (!toastContainer) return;

      const toast = createToastElement(type, data);

      // If already showing, queue it
      if (isShowing) {
        toastQueue.push({ type, data, duration });
        return;
      }

      isShowing = true;
      currentToast = toast;

      // Add to DOM
      toastContainer.appendChild(toast);

      // Trigger animation
      requestAnimationFrame(() => {
        toast.classList.add('cs-toast--show');
      });

      // Auto-hide after duration
      setTimeout(() => {
        hideToast(toast);
      }, duration);
    }

    /**
     * Hide a toast notification
     */
    function hideToast(toast) {
      toast.classList.remove('cs-toast--show');
      toast.classList.add('cs-toast--hide');

      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
        isShowing = false;
        currentToast = null;

        // Show next queued toast
        if (toastQueue.length > 0) {
          const next = toastQueue.shift();
          showToast(next.type, next.data, next.duration);
        }
      }, TOAST_ANIMATION_DURATION);
    }

    /**
     * Show achievement toast
     */
    function showAchievement(achievement) {
      showToast('achievement', achievement);
    }

    /**
     * Show level-up toast
     */
    function showLevelUp(data) {
      showToast('levelup', data);
    }

    /**
     * Show XP gained toast
     */
    function showXpGained(xp, message) {
      showToast('xp', { xp, message }, 2000); // Shorter duration for XP
    }

    /**
     * Escape HTML to prevent XSS
     */
    function escapeHtml(text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    if (window.cyberSellsConfig?.debug) {
      console.log('[CyberSells] Toast system initialized');
    }

    return {
      showAchievement,
      showLevelUp,
      showXpGained,
      showToast
    };
  }

  // Expose module
  window.CyberSells_toast = {
    createToastSystem
  };

})();
